import { useCallback, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

/**
 * The header search searches bookings and vouchers at once
 * (GET /api/v1/globalSearch). The backend gates each group on its own right, so
 * a user who may only read one of the two still gets a working box instead of a
 * 403 on every keystroke.
 */

/** Shortest query the backend accepts. */
export const MIN_GLOBAL_SEARCH_LENGTH = 3;

/** Suggestions per group; a full mail address raises this backend-side. */
const SUGGESTION_LIMIT = 6;

const EMPTY_RESULT = {
  searchQuery: '',
  isMailQuery: false,
  bookings: { total: 0, items: [], allowed: false },
  vouchers: { total: 0, items: [], allowed: false },
};

const useGlobalSearch = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, setError, sendRequest } = useHttp();

  const searchBookingsAndVouchers = useCallback(
    async searchQuery => {
      const query = String(searchQuery || '').trim();
      if (query.length < MIN_GLOBAL_SEARCH_LENGTH) return EMPTY_RESULT;

      const response = await sendRequest({
        url: '/api/v1/globalSearch',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params: { searchQuery: query, limit: SUGGESTION_LIMIT },
      });

      return {
        ...EMPTY_RESULT,
        ...(response || {}),
        bookings: response?.bookings || EMPTY_RESULT.bookings,
        vouchers: response?.vouchers || EMPTY_RESULT.vouchers,
      };
    },
    [authCtx, sendRequest]
  );

  return { isLoading, error, setError, searchBookingsAndVouchers, EMPTY_RESULT };
};

export default useGlobalSearch;
